import React, { useState } from 'react';
import MaterialTable from 'material-table';
import Avatar from '@material-ui/core/Avatar';
import Chip from '@material-ui/core/Chip';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import IconButton from '@material-ui/core/IconButton';
import { supprimerSertisseur, modifierSertisseur } from '../action/AtelierAction';
import SertisseurEditModal from './SertisseurEditModal';
import { imagesRepo } from '../../../config';

const formatWeight = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? `${parsed.toFixed(2)} g` : '-';
};

const formatPrice = (value) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) {
        return '-';
    }
    return `${parsed.toFixed(2)} EUR`;
};

const SertisseurMovements = ({
    data = [],
    onRowClick,
    onDelete,
    onRefresh,
    selectedMovement,
    minimal = false,
    useListView = false
}) => {
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editingRow, setEditingRow] = useState(null);
    const [editLoading, setEditLoading] = useState(false);

    const handleDelete = async (rowData) => {
        // Si mode minimal (SAISIES), suppression locale uniquement, sans confirmation
        if (minimal) {
            if (onDelete) {
                onDelete(rowData.id_ligne);
            }
            return;
        }

        // Mode normal (historique OF) : confirmation + suppression BD
        if (window.confirm(`Supprimer ${rowData.label} ?`)) {
            try {
                await supprimerSertisseur(rowData);
                if (onDelete) {
                    onDelete(rowData.id_ligne);
                }
                if (onRefresh) {
                    onRefresh();
                }
            } catch (error) {
                console.error("Erreur suppression:", error);
                alert("Erreur suppression");
            }
        }
    };

    const handleEditOpen = (rowData) => {
        setEditingRow(rowData);
        setEditModalOpen(true);
    };

    const handleEditClose = () => {
        setEditModalOpen(false);
        setEditingRow(null);
    };

    const handleEditSave = async (values) => {
        setEditLoading(true);
        try {
            await modifierSertisseur(values);
            handleEditClose();
            if (onRefresh) {
                onRefresh();
            }
        } catch (error) {
            console.error('Erreur mise à jour:', error);
            alert('Erreur mise à jour');
        } finally {
            setEditLoading(false);
        }
    };

    const columns = [
        {
            title: 'Nom',
            field: 'label',
            align: 'center',
            width: '12%'
        },
        {
            title: 'N° OF',
            field: 'of_serto',
            align: 'center',
            width: '10%'
        },
        {
            title: 'Ref CV',
            field: 'ref',
            align: 'center',
            width: '12%',
            render: row => row.ref || '-'
        },
        {
            title: 'Ref',
            field: 'refDesc',
            align: 'center',
            width: '15%',
            render: row => row.refDesc || '-'
        },
        {
            title: 'Poids D',
            field: 'pds_d',
            align: 'center',
            width: '10%',
            render: row => row.pds_d ? parseFloat(row.pds_d).toFixed(2) + ' g' : '-'
        },
        {
            title: 'Poids R',
            field: 'pds_r',
            align: 'center',
            width: '10%',
            render: row => row.pds_r ? parseFloat(row.pds_r).toFixed(2) + ' g' : '-'
        },
        {
            title: 'Prix',
            field: 'prix',
            align: 'center',
            width: '8%',
            render: row => row.prix || '-'
        },
        {
            title: 'Statut',
            align: 'center',
            width: '10%',
            render: row => {
                const isEnCours = row.rendu_serti === 0 || row.rendu_serti === '0';
                return (
                    <Chip
                        label={isEnCours ? 'En cours' : 'Fini'}
                        color={isEnCours ? 'default' : 'primary'}
                        size="small"
                        style={{
                            backgroundColor: isEnCours ? '#ff9800' : '#4caf50',
                            color: '#fff',
                            fontWeight: 500
                        }}
                    />
                );
            }
        }
    ];

    if (minimal) {
        return (
            <List style={{ padding: 0 }}>
            {data.length === 0 ? (
                <div style={{ padding: '12px', textAlign: 'center', color: '#999', fontSize: '0.9rem' }}>
                Aucune saisie
                </div>
            ) : (
                data.map((item, idx) => (
                <ListItem
                    key={idx}
                    button
                    onClick={() => onRowClick && onRowClick(item)}
                    selected={selectedMovement?.id_ligne === item.id_ligne}
                    style={{
                    backgroundColor: selectedMovement?.id_ligne === item.id_ligne ? '#e3f2fd' : 'transparent',
                    padding: '8px 12px',
                    borderBottom: '1px solid #eee'
                    }}
                >
                    <Avatar
                    src={item.avatar ? `${imagesRepo}/avatar/${item.avatar}` : undefined}
                    style={{
                        width: 32,
                        height: 32,
                        marginRight: '12px',
                        backgroundColor: '#1976d2',
                        fontSize: '0.9rem'
                    }}
                    >
                    {!item.avatar && item.label ? item.label.charAt(0).toUpperCase() : '?'}
                    </Avatar>
                    <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flex: 1,
                        marginRight: 40,
                        gap: 16,
                    }}
                    >
                    <div
                        style={{
                        display: 'flex',
                        flexDirection: 'column',
                        minWidth: 90,
                        fontSize: '0.8rem',
                        color: '#607d8b',
                        }}
                    >
                        <span style={{ fontWeight: 600 }}>OF {item.of_serto}</span>
                        <span style={{ color: '#263238', fontWeight: 500 }}>{item.label}</span>
                    </div>
                    <div
                        style={{
                        display: 'flex',
                        flexDirection: 'column',
                        fontSize: '0.78rem',
                        color: '#455a64',
                        textAlign: 'center',
                        gap: 2,
                        minWidth: 60,
                        }}
                    >
                        <span style={{ fontWeight: 600 }}>Pds D</span>
                        <span>{formatWeight(item.pds_d)}</span>
                    </div>
                    <div
                        style={{
                        display: 'flex',
                        flexDirection: 'column',
                        fontSize: '0.78rem',
                        color: '#455a64',
                        textAlign: 'center',
                        gap: 2,
                        minWidth: 60,
                        }}
                    >
                        <span style={{ fontWeight: 600 }}>Pds R</span>
                        <span>{formatWeight(item.pds_r)}</span>
                    </div>
                    <div
                        style={{
                        display: 'flex',
                        flexDirection: 'column',
                        fontSize: '0.78rem',
                        color: '#455a64',
                        textAlign: 'center',
                        gap: 2,
                        minWidth: 70,
                        }}
                    >
                        <span style={{ fontWeight: 600 }}>Prix</span>
                        <span>{formatPrice(item.prix)}</span>
                    </div>
                    </div>
                    <ListItemSecondaryAction>
                    <IconButton
                        edge="end"
                        size="small"
                        onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(item);
                        }}
                    >
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                    </ListItemSecondaryAction>
                </ListItem>
                ))
            )}
            </List>
        );
    }

    // Vue table (historique)
    // Trier les données: d'abord par statut (en cours=0 en premier), puis par nom
    const sortedData = [...data].sort((a, b) => {
        const aStatus = a.rendu_serti === 0 || a.rendu_serti === '0' ? 0 : 1;
        const bStatus = b.rendu_serti === 0 || b.rendu_serti === '0' ? 0 : 1;
        
        // D'abord par statut
        if (aStatus !== bStatus) {
            return aStatus - bStatus;
        }
        
        // Puis par nom
        return (a.label || '').localeCompare((b.label || ''));
    });

    return (
        <>
            <MaterialTable
                columns={columns}
                data={sortedData}
                title=""
                options={{
                    search: false,
                    paging: false,
                    toolbar: false,
                    headerStyle: {
                        backgroundColor: '#f5f5f5',
                        fontWeight: 'bold',
                        fontSize: '0.85rem'
                    },
                    rowStyle: (rowData) => ({
                        backgroundColor: selectedMovement?.id_ligne === rowData.id_ligne ? '#e3f2fd' : '#fff'
                    }),
                    actionsColumnIndex: -1
                }}
                actions={[
                    rowData => ({
                        icon: () => <EditIcon />,
                        tooltip: 'Modifier',
                        onClick: (event, rowData) => handleEditOpen(rowData),
                        disabled: rowData.pesee !== 0 && rowData.pesee !== '0'
                    }),
                    rowData => ({
                        icon: () => <DeleteIcon />,
                        tooltip: 'Supprimer',
                        onClick: (event, rowData) => handleDelete(rowData),
                        disabled: rowData.pesee !== 0 && rowData.pesee !== '0'
                    })
                ]}
                onRowClick={(event, rowData) => {
                    onRowClick && onRowClick(rowData);
                }}
            />

            {editingRow && (
                <SertisseurEditModal
                    open={editModalOpen}
                    onClose={handleEditClose}
                    rowData={editingRow}
                    onSave={handleEditSave}
                    loading={editLoading}
                />
            )}
        </>
    );
};

export default SertisseurMovements;
